import { useEffect, useMemo, useState } from "react";
import "./SimplePackingList.scss";
import SupplierService from "../../../services/SupplierService";
import UIService from "../../../services/UIService";
import { OwViewOptionsPanel } from "../OwViewOptionsPanel/OwViewOptionsPanel";
import { usePackingListViewOptionsUI } from "../../../hooks/packing_list/usePackingListViewOptionsUI";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import OwPrintButton from "../OwPrintButton/OwPrintButton";
import OwSelectOption from "../OwSelectOption/OwSelectOption";
import OwButton from "../OwButton/OwButton";
import SupplierGuidEnum from "../../../models/SupplierGuidEnum";
import { IOption } from "../../../models/IOption";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import { useLocation } from "react-router-dom";
import { SimplePackingListDetail } from "./SimplePackingListDetail/SimplePackingListDetail";
import IOrder from "../../../models/IOrder";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnUpdateSupplierSettingsDTO from "../../../models/DTO/IOnUpdateSupplierSettingsDTO";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import IOnVermontAvery8136DTO from "../../../models/DTO/IOnVermontAvery8136DTO";
import IUpdateSupplierSettingsDTO from "../../../models/DTO/IUpdateSupplierSettingsDTO";
import { usePackingListViewOptions } from "../../../hooks/packing_list/usePackingListViewOptions";
import * as _ from "lodash-es";
import BulkOrderSubmitModal from "../../orders/BulkOrderSubmitModal/BulkOrderSubmitModal";
import IVermontAvery8136DTO from "../../../models/DTO/IVermontAvery8136DTO";
import IOnGetOrdersPackingListDTO from "../../../models/DTO/IOnGetOrdersPackingListDTO";
import { VerticalLine } from "../VerticalLine/VerticalLine";
import { ControlBar } from "../ControlBar/ControlBar";
import { useAppContextNonNull } from "@contexts/AppContext";
import { useDailyRouteGroups } from "../../../hooks/useDailyRouteGroups";
import { DailyRouteCoverSheet } from "../DailyRouteCoverSheet/DailyRouteCoverSheet";
import moment from "moment";

type Props = {};

export const SimplePackingList = (props: Props) => {
    const { supplierModel, userModel, uiContext, socket } = useAppContextNonNull();

    const location = useLocation();
    const isFromDailyRoutes = location.pathname.includes("routes/daily");
    const { dailyRouteGroups, allOrderGuids: dailyRouteOrderGuids, loading: dailyRoutesLoading, hasDailyRoutes } = useDailyRouteGroups();

    const supplierSettings = SupplierService.getSupplierSettings(supplierModel);

    const [viewOptions, handleViewOptionChange] = usePackingListViewOptions(supplierSettings);

    const optionsPanelViewOptions = usePackingListViewOptionsUI(viewOptions, supplierModel);

    const [selectedTemplate, setSelectedTemplate] = useState<"STANDARD" | "MINIMAL">("STANDARD");
    const [orders, setOrders] = useState<IOrder[]>([]);
    const [orderGuids, setOrderGuids] = useState<string[]>([]);

    const onGetOrdersPackingList = (data: IOnGetOrdersPackingListDTO) => {
        const orderNames = data.orders.map((order) => {
            return order.name;
        });

        document.title = `Orderwerks >> Packing List >> ${orderNames.join(", ")}`;

        setOrders(data.orders);
    };

    const onUpdateSupplierSettings = (data: IOnUpdateSupplierSettingsDTO) => {
        const message: INotification = {
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
            message: "successfully_updated_settings",
        };

        uiContext.notifyFn(null, message);
    };

    const onDownloadAveryLabels = (data: IOnVermontAvery8136DTO) => {
        UIService.downloadDataFile(data.pdfData, "AveryLabels.pdf", "application/pdf");
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.OrderOnGetOrdersPackingList, fn: onGetOrdersPackingList },
        { endpoint: EndpointsEnum.SupplierOnUpdateSupplierSettings, fn: onUpdateSupplierSettings },
        { endpoint: EndpointsEnum.PackingListOnGenerateAveryLabels, fn: onDownloadAveryLabels },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        if (!hasDailyRoutes) {
            const params = new URLSearchParams(location.search);
            const _orders = params.get("orders") || "";
            setOrderGuids(_orders.split(","));
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        if (hasDailyRoutes && !dailyRoutesLoading && dailyRouteOrderGuids.length > 0) {
            setOrderGuids(dailyRouteOrderGuids);
        }
    }, [hasDailyRoutes, dailyRoutesLoading, dailyRouteOrderGuids]);

    useEffect(() => {
        if (orderGuids.length) {
            getOrderForPackingList();
        }
    }, [orderGuids]);

    const packingListTemplates: IOption[] = [
        { value: "STANDARD", label: "8.5x11" },
        { value: "MINIMAL", label: "4x6" },
    ];

    const navItems: IBreadcrumbs[] = useMemo(() => {
        const items: IBreadcrumbs[] = [];

        if (isFromDailyRoutes) {
            items.push({ title: "Daily Routes", url: "/routes/daily" });
        } else if (location.pathname.indexOf("shipments") >= 0) {
            items.push({ title: "Shipments", url: "/shipments" });
        } else {
            items.push({ title: "Orders", url: "/orders" });
        }

        items.push({ title: "Packing List", url: "" });

        return items;
    }, [isFromDailyRoutes, location.pathname]);

    const getOrderForPackingList = () => {
        if (userModel && supplierModel) {
            const _orderGuids = orderGuids.map((guid) => {
                return { guid };
            });

            const payload: any = {
                orders: _orderGuids,
                supplierGuid: supplierModel.guid,
                userGuid: userModel.guid,
                userToken: userModel.token,
            };

            socket.emit(EndpointsEnum.OrderGetOrdersPackingList, payload);
        }
    };

    const closeModalFn = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const submitOrders = () => {
        const modalComponent = (
            <BulkOrderSubmitModal
                orderGuids={orderGuids}
                supplierModel={supplierModel}
                userModel={userModel}
                closeModalFn={closeModalFn}
                socket={socket}
                notifyFn={uiContext.notifyFn}
                selectAll={true}
            />
        );

        uiContext.setModalComponent(modalComponent);
        uiContext.showModal(true);
    };

    const downloadAveryLabels = () => {
        if (orderGuids) {
            const _orderGuids = orderGuids.reverse();

            const payload: IVermontAvery8136DTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                orderGuids: _orderGuids,
            };

            socket.emit(EndpointsEnum.PackingListGenerateAveryLabels, payload);
        }
    };

    const saveViewOptions = () => {
        const payload: IUpdateSupplierSettingsDTO = {
            supplierGuid: supplierModel.guid,
            userGuid: userModel.guid,
            userToken: userModel.token,
            // @ts-expect-error
            settings: {
                simple_packing_list: {
                    show_phone: viewOptions.showPhone,
                    show_email: viewOptions.showEmail,
                    show_order_qty: viewOptions.showOrderQty,
                    show_ship_qty: viewOptions.showShipQty,
                    show_dropshipment: viewOptions.showDropshipment,
                    show_order_desc: viewOptions.showOrderDescription,
                    show_sku: viewOptions.showSku,
                    show_upc: viewOptions.showUpc,
                    hide_sku_col: viewOptions.hideSkuCol,
                    show_ship_to_phone: viewOptions.showShipToPhone,
                    show_bill_to_phone: viewOptions.showBillToPhone,
                    show_order_user: viewOptions.showOrderUser,
                    show_row_bin: viewOptions.showItemLocation,
                    show_uom: viewOptions.showUom,
                    inline_logo_name: viewOptions.inlineLogoName,
                    show_bill_to: viewOptions.showBillTo,
                    show_item_photo: viewOptions.showItemPhoto,
                    hide_business_name: viewOptions.hideBusinessName,
                    show_dimensions: viewOptions.showDimensions,
                    show_item_location: viewOptions.showItemLocation,
                    show_unit_price: viewOptions.showUnitPrice,
                },
            },
        };

        socket.emit(EndpointsEnum.SupplierUpdateSupplierSettings, payload);
    };

    const selectTemplate = (template: IOption) => {
        setSelectedTemplate(template.value as "STANDARD" | "MINIMAL");
    };

    const leftGroup = (
        <>
            <div className={"d-inline-flex"}>
                <div className={"me-2"}>
                    <OwSelectOption
                        valueProp={"value"}
                        titleProp={"label"}
                        fn={selectTemplate}
                        name={"template"}
                        options={packingListTemplates}
                        emitObject={true}
                        selectedOption={"selectedTemplate"}
                        hasSelectedOptionProp={false}
                    />
                </div>
                <OwPrintButton />
            </div>
        </>
    );

    const centerGroup = supplierModel.show_submit_on_packing_list ? <OwButton title={"submit_orders"} fn={submitOrders} /> : null;

    const rightGroup =
        supplierModel.guid === SupplierGuidEnum.VERMONT ? (
            <>
                <VerticalLine />
                <OwButton title={"labels_pdf_download"} fn={downloadAveryLabels} />
            </>
        ) : null;

    return (
        <div className={"SimplePackingList mb-5"}>
            <OwViewOptionsPanel
                saveFn={saveViewOptions}
                showSaveBtn={true}
                viewOptions={optionsPanelViewOptions}
                onChangeFn={handleViewOptionChange}
            />

            {/* MAIN HEADER SECTION */}
            <div className={"row mb-2 d-flex justify-content-between align-items-center flex-wrap d-print-none"}>
                <div className={"d-inline-flex mt-2"}>
                    <Breadcrumbs hasTopMargin={false} navItems={navItems} />
                </div>
            </div>

            <div className="row">
                <div className="col-12 text-end">
                    <ControlBar
                        sections={[
                            { name: "left", elements: [leftGroup] },
                            { name: "center", elements: [centerGroup] },
                            { name: "right", elements: [rightGroup] },
                        ]}
                    />
                </div>
            </div>

            {/* MAIN SECTION */}
            {hasDailyRoutes && dailyRouteGroups.length > 0
                ? dailyRouteGroups.map((group) => {
                      const groupOrders = orders.filter((order) => group.orderGuids.includes(order.guid));
                      if (groupOrders.length === 0) return null;

                      return (
                          <div key={group.dailyRoute.guid} className={"DailyRouteGroup"}>
                              <DailyRouteCoverSheet
                                  dailyRouteName={group.dailyRoute.name || ""}
                                  routeName={group.dailyRoute.Route?.name || ""}
                                  routeDate={moment(group.dailyRoute.route_date).format("MM/DD/YYYY")}
                                  driverName={group.dailyRoute.Driver?.name || "-"}
                                  vehicleName={group.dailyRoute.Vehicle?.name || "-"}
                                  orderCount={groupOrders.length}
                              />
                              {groupOrders.map((order) => (
                                  <div key={order.guid} className={"PackingListContainer"}>
                                      <SimplePackingListDetail
                                          order={order}
                                          sortCol={"item_no"}
                                          template={selectedTemplate}
                                          viewOptions={viewOptions}
                                      />
                                  </div>
                              ))}
                          </div>
                      );
                  })
                : orders.length
                  ? orders.map((order) => {
                        return (
                            <div key={order.guid} className={"PackingListContainer"}>
                                <SimplePackingListDetail
                                    order={order}
                                    sortCol={"item_no"}
                                    template={selectedTemplate}
                                    viewOptions={viewOptions}
                                />
                            </div>
                        );
                    })
                  : null}
        </div>
    );
};
